/**
 * Điều khiển game cho UI (M2): giữ TrangThaiGame, nối core với render/vòng lặp,
 * lưu/tải localStorage, tính báo cáo offline khi mở lại.
 *
 * UI chỉ đọc state qua controller và gọi lệnh — không chứa luật chơi.
 * Mọi lệnh đều gọi core (src/core) rồi thông báo cho UI vẽ lại.
 */
import { datChinhSach, datNguongChung, datNguongRieng, taoGameMoi } from '../core/game.js';
import { thueNhanVien } from '../core/cua-hang.js';
import { diChuyen } from '../core/khu-vuc.js';
import { chapNhanDonLon, giaoDonLon } from '../core/nhiem-vu.js';
import { tangQua } from '../core/npc.js';
import { dungCosmetic, muaCosmetic } from '../core/cosmetic.js';
import {
  docDongY,
  datDongY,
  docHangDoi,
  xoaHangDoi,
  guiBaoCao,
  type DongYCrash,
} from '../crash.js';
import { EngineAmThanh } from '../audio/engine.js';
import { datNoiThat, xoaNoiThat, boCucMacDinh, MA_QUAY_THU_NGAN } from '../core/noi-that.js';
import { nangCap } from '../core/cua-hang.js';
import { tick } from '../core/dong-ho.js';
import { gioTrongNgay } from '../core/khach.js';
import { thoiTietHienTaiCuaKhu } from '../core/thoi-tiet.js';
import { taiDuLieu } from '../core/du-lieu.js';
import { apDungOffline } from '../core/offline.js';
import { nhapJson, xuatJson } from '../core/luu-tru.js';
import { taoRng, type HamRng } from '../core/rng.js';
import type { BaoCaoOffline, ChinhSachGiaChon, DuLieuGame, TrangThaiGame } from '../core/types.js';
import { LuuTruTuDong } from '../luu-tru.js';

const KHOA_CAI_DAT = 'idle-shop-settings';

export interface CaiDatGame {
  bloom: boolean;
  /** true = độ phân giải đầy đủ; false = 1/2 (mặc định, master-plan 3.5). */
  doPhanGiaiDayDu: boolean;
  giamChuyenDong: boolean;
  tocDo: number;
  /** M3 — tiếp cận (master-plan 3.5): cỡ chữ, tắt rung, tắt hiệu ứng. */
  coChuLon: boolean;
  tatRung: boolean;
  tatHieuUng: boolean;
  nhacBat: boolean;
  sfxBat: boolean;
}

const CAI_DAT_MAC_DINH: CaiDatGame = {
  bloom: true,
  doPhanGiaiDayDu: false,
  giamChuyenDong: false,
  tocDo: 1,
  coChuLon: false,
  tatRung: false,
  tatHieuUng: false,
  nhacBat: true,
  sfxBat: true,
};

function docCaiDat(): CaiDatGame {
  try {
    const raw = localStorage.getItem(KHOA_CAI_DAT);
    if (raw) {
      const doc = JSON.parse(raw) as Partial<CaiDatGame>;
      return { ...CAI_DAT_MAC_DINH, ...doc };
    }
  } catch {
    // Bỏ qua — dùng mặc định.
  }
  return { ...CAI_DAT_MAC_DINH };
}

export class GameController {
  readonly duLieu: DuLieuGame;
  state: TrangThaiGame;
  caiDat: CaiDatGame;
  baoCaoOffline: BaoCaoOffline | null = null;
  readonly amThanh: EngineAmThanh;

  private readonly rng: HamRng;
  private readonly luuTru: LuuTruTuDong;
  private readonly nguoiNghe = new Set<() => void>();

  constructor() {
    this.duLieu = taiDuLieu();
    this.rng = taoRng((Date.now() ^ (Math.random() * 1e9)) >>> 0);
    this.luuTru = new LuuTruTuDong(localStorage);
    this.caiDat = docCaiDat();
    this.amThanh = new EngineAmThanh();
    this.amThanh.datNhacBat(this.caiDat.nhacBat);
    this.amThanh.datSfxBat(this.caiDat.sfxBat);

    const banLuuRaw = localStorage.getItem('idle-shop-save');
    if (banLuuRaw) {
      this.state = nhapJson(banLuuRaw);
      this.suaChuaBoCuc();
      // Tính offline từ lúc lưu.
      try {
        const lucLuu = Date.parse((JSON.parse(banLuuRaw) as { lucLuu: string }).lucLuu);
        const gioVang = (Date.now() - lucLuu) / 3_600_000;
        if (gioVang >= 1 / 60) {
          this.baoCaoOffline = apDungOffline(this.state, this.duLieu, gioVang, this.rng);
        }
      } catch {
        this.baoCaoOffline = null;
      }
    } else {
      this.state = taoGameMoi(this.duLieu, this.rng);
      this.luu();
    }

    this.luuTru.batDauTuDong(() => this.state);
    window.addEventListener('pagehide', () => this.luu());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.luu();
    });
  }

  /** Bản lưu cũ (v1) không có quầy: dựng lại bố cục mặc định miễn phí. */
  private suaChuaBoCuc(): void {
    if (!this.state.boCuc.some((m) => m.ma === MA_QUAY_THU_NGAN)) {
      const macDinh = boCucMacDinh(this.state.capCuaHang, this.duLieu);
      const dich = Math.max(0, ...this.state.boCuc.map((m) => m.uid)) + 1;
      this.state.boCuc = macDinh.map((m, i) => ({ ...m, uid: dich + i }));
      this.state.uidNoiThatTiep = dich + macDinh.length;
    }
  }

  /** Đăng ký vẽ lại khi state đổi. Trả về hàm hủy đăng ký. */
  theoDoi(fn: () => void): () => void {
    this.nguoiNghe.add(fn);
    return () => {
      this.nguoiNghe.delete(fn);
    };
  }

  private thongBao(): void {
    for (const fn of this.nguoiNghe) fn();
  }

  /** Một giờ game trôi qua (vòng lặp gọi). */
  tickMoiGio(): void {
    tick(this.state, this.duLieu, this.rng);
    this.capNhatAmThanh();
    this.thongBao();
  }

  luu(): void {
    this.luuTru.luu(this.state);
  }

  datCaiDat(patch: Partial<CaiDatGame>): void {
    this.caiDat = { ...this.caiDat, ...patch };
    try {
      localStorage.setItem(KHOA_CAI_DAT, JSON.stringify(this.caiDat));
    } catch {
      // Bỏ qua.
    }
    this.amThanh.datNhacBat(this.caiDat.nhacBat);
    this.amThanh.datSfxBat(this.caiDat.sfxBat);
    if (this.caiDat.nhacBat || this.caiDat.sfxBat) this.capNhatAmThanh();
    this.thongBao();
  }

  /** Mở khóa audio sau cử chỉ người dùng đầu tiên (rồi phát nhạc theo khu/giờ). */
  moKhoaAmThanh(): void {
    this.amThanh.moKhoa();
    this.capNhatAmThanh();
  }

  /**
   * B3: đồng bộ âm thanh theo trạng thái game — track nhạc theo khu vực +
   * giờ game, ambient mưa theo thời tiết khu hiện tại. Gọi sau mở khóa,
   * đổi khu, tick mỗi giờ, đổi cài đặt. datNhacTheoKhuVaGio tự no-op khi
   * track không đổi.
   */
  private capNhatAmThanh(): void {
    this.amThanh.datNhacTheoKhuVaGio(this.state.khuHienTai, gioTrongNgay(this.state.tongGioGame));
    this.amThanh.datMuaDangRoi(
      thoiTietHienTaiCuaKhu(this.state, this.duLieu, this.state.khuHienTai).ma === 'mua',
    );
  }

  // --- Lệnh game (đều qua core) ---

  chonChinhSach(chinhSach: ChinhSachGiaChon): void {
    datChinhSach(this.state, chinhSach);
    this.thongBao();
  }

  datNguongNhapChung(nguong: number | undefined): void {
    datNguongChung(this.state, nguong);
    this.luu();
    this.thongBao();
  }

  datNguongNhapRieng(ma: string, nguong: number | undefined): void {
    datNguongRieng(this.state, ma, nguong);
    this.luu();
    this.thongBao();
  }

  nangCapCuaHang(): boolean {
    const ok = nangCap(this.state, this.duLieu);
    if (ok) {
      this.luu();
      this.thongBao();
    }
    return ok;
  }

  datNoiThatMoi(ma: string, x: number, y: number): void {
    datNoiThat(this.state, this.duLieu, ma, x, y);
    this.luu();
    this.thongBao();
  }

  doNoiThat(uid: number): void {
    xoaNoiThat(this.state, this.duLieu, uid);
    this.luu();
    this.thongBao();
  }

  hoanThanhTutorial(): void {
    this.state.tutorialXong = true;
    this.luu();
    this.thongBao();
  }

  nhanBaoCaoOffline(): void {
    this.baoCaoOffline = null;
    this.luu();
    this.thongBao();
  }

  // --- Lệnh M3 ---

  /** Di chuyển tới khu vực (bản đồ thế giới). */
  denKhu(maKhu: string): void {
    diChuyen(this.state, this.duLieu, maKhu);
    this.capNhatAmThanh();
    this.luu();
    this.thongBao();
  }

  /** Thuê nhân viên. */
  thueNhanVien(vai: 'thuNgan' | 'xepKe'): void {
    thueNhanVien(this.state, this.duLieu, vai);
    this.luu();
    this.thongBao();
  }

  /** Nhận đơn lớn. */
  nhanDonLon(): void {
    chapNhanDonLon(this.state);
    this.luu();
    this.thongBao();
  }

  /** Giao đơn lớn; trả về thưởng. */
  giaoDonLon(): { xu: number; xuCaoCap: number; xp: number } {
    const kq = giaoDonLon(this.state, this.duLieu);
    this.luu();
    this.thongBao();
    return kq;
  }

  /** Tặng quà cho NPC; trả về { lenCap, capMoi }. */
  tangQuaChoNpc(maNpc: string): { lenCap: boolean; capMoi: number } {
    const kq = tangQua(this.state, this.duLieu, maNpc);
    this.luu();
    this.thongBao();
    return kq;
  }

  /** Mua cosmetic bằng xu cao cấp. */
  muaCosmetic(ma: string): void {
    muaCosmetic(this.state, this.duLieu, ma);
    this.luu();
    this.thongBao();
  }

  /** Dùng cosmetic (trang trí / đồng phục). */
  dungCosmetic(ma: string): void {
    dungCosmetic(this.state, this.duLieu, ma);
    this.luu();
    this.thongBao();
  }

  // --- Báo lỗi crash (ẩn danh) ---

  docDongYCrash(): DongYCrash {
    return docDongY();
  }

  datDongYCrash(cheDo: DongYCrash): void {
    datDongY(cheDo);
    this.thongBao();
  }

  demBaoCaoCrashCho(): number {
    return docHangDoi().length;
  }

  async guiBaoCaoCrash(): Promise<boolean> {
    const hang = docHangDoi();
    let ok = true;
    for (const bc of hang) {
      if (!(await guiBaoCao(bc))) ok = false;
    }
    if (ok) xoaHangDoi();
    this.thongBao();
    return ok;
  }

  boQuaBaoCaoCrash(): void {
    xoaHangDoi();
    this.thongBao();
  }

  xuatLuu(): string {
    return xuatJson(this.state);
  }

  nhapLuu(json: string): void {
    this.state = nhapJson(json);
    this.suaChuaBoCuc();
    this.baoCaoOffline = null;
    this.luu();
    this.thongBao();
  }

  xoaLuu(): void {
    this.luuTru.xoa();
  }

  huy(): void {
    this.luuTru.dungTuDong();
    this.amThanh.huy();
    this.nguoiNghe.clear();
  }
}
